import React, { useCallback, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import Support from "@/pages/Support";
import Privacy from "@/pages/Privacy";
import AuthCallback from "@/pages/AuthCallback";
import ClientHome from "@/pages/ClientHome";
import CategoryPage from "@/pages/CategoryPage";
import Quotations from "@/pages/Quotations";
import NewQuotation from "@/pages/NewQuotation";
import QuickInquiry from "@/pages/QuickInquiry";
import QuotationDetail from "@/pages/QuotationDetail";
import ActiveOrders from "@/pages/ActiveOrders";
import OrderDetail from "@/pages/OrderDetail";
import More from "@/pages/More";
import Profile from "@/pages/Profile";
import Documents from "@/pages/Documents";
import Projects from "@/pages/Projects";
import Help from "@/pages/Help";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminOrderEdit from "@/pages/AdminOrderEdit";
import AdminNewOrder from "@/pages/AdminNewOrder";
import AdminClients from "@/pages/AdminClients";
import AdminProducts from "@/pages/AdminProducts";
import AdminCategories from "@/pages/AdminCategories";
import AdminComparison from "@/pages/AdminComparison";
import AdminQuotations from "@/pages/AdminQuotations";
import AdminQuotationDetail from "@/pages/AdminQuotationDetail";
import AdminCompanies from "@/pages/AdminCompanies";
import CompleteProfile from "@/pages/CompleteProfile";
import ProductDetail from "@/pages/ProductDetail";
import PublicTrack from "@/pages/PublicTrack";
import PublicProductSpecs from "@/pages/PublicProductSpecs";
import SplashScreen from "@/components/SplashScreen";

function ProtectedRoute({ children, requireAdmin, allowGuest }) {
  const { user, isGuest, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50">
        <div className="mt-tiny text-zinc-500">Loading...</div>
      </div>
    );
  }
  if (!user) {
    // Guests may browse the catalog; everything else bounces to sign-in.
    if (allowGuest && isGuest && !requireAdmin) return children;
    return <Navigate to="/" replace state={{ from: location }} />;
  }
  if (requireAdmin && user.role !== "admin") return <Navigate to="/home" replace />;
  // Onboarding gate: clients without a complete profile must finish onboarding first.
  if (
    user.role === "client" &&
    !user.profile_complete &&
    location.pathname !== "/complete-profile"
  ) {
    return <Navigate to="/complete-profile" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth callback (session_id in URL hash) before any protected routes
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingRedirect />} />

      {/* Friendly aliases requested by owner */}
      <Route path="/track/:orderId" element={<PublicTrack />} />
      <Route path="/specs/:productId" element={<PublicProductSpecs />} />
      <Route path="/support" element={<Support />} />
      <Route path="/privacy-policy" element={<Privacy />} />
      <Route path="/portal" element={<Navigate to="/home" replace />} />
      <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />

      {/* Client */}
      <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>} />
      <Route path="/home" element={<ProtectedRoute allowGuest><ClientHome /></ProtectedRoute>} />
      <Route path="/category/:categoryId" element={<ProtectedRoute allowGuest><CategoryPage /></ProtectedRoute>} />
      <Route path="/products/:productId" element={<ProtectedRoute allowGuest><ProductDetail /></ProtectedRoute>} />
      <Route path="/quotations" element={<ProtectedRoute><Quotations /></ProtectedRoute>} />
      <Route path="/quotations/new" element={<ProtectedRoute><NewQuotation /></ProtectedRoute>} />
      <Route path="/quotations/quick" element={<ProtectedRoute><QuickInquiry /></ProtectedRoute>} />
      <Route path="/quotations/:quotationId" element={<ProtectedRoute><QuotationDetail /></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><ActiveOrders /></ProtectedRoute>} />
      <Route path="/orders/:orderId" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
      <Route path="/more" element={<ProtectedRoute><More /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/documents/:kind" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />

      {/* Back-compat: /dashboard → /home */}
      <Route path="/dashboard" element={<Navigate to="/home" replace />} />

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/new" element={<ProtectedRoute requireAdmin><AdminNewOrder /></ProtectedRoute>} />
      <Route path="/admin/clients" element={<ProtectedRoute requireAdmin><AdminClients /></ProtectedRoute>} />
      <Route path="/admin/products" element={<ProtectedRoute requireAdmin><AdminProducts /></ProtectedRoute>} />
      <Route path="/admin/categories" element={<ProtectedRoute requireAdmin><AdminCategories /></ProtectedRoute>} />
      <Route path="/admin/comparison" element={<ProtectedRoute requireAdmin><AdminComparison /></ProtectedRoute>} />
      <Route path="/admin/quotations" element={<ProtectedRoute requireAdmin><AdminQuotations /></ProtectedRoute>} />
      <Route path="/admin/quotations/:quotationId" element={<ProtectedRoute requireAdmin><AdminQuotationDetail /></ProtectedRoute>} />
      <Route path="/admin/companies" element={<ProtectedRoute requireAdmin><AdminCompanies /></ProtectedRoute>} />
      <Route path="/admin/orders/:orderId" element={<ProtectedRoute requireAdmin><AdminOrderEdit /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function LandingRedirect() {
  const { user, isGuest, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50">
        <div className="mt-tiny text-zinc-500">Loading...</div>
      </div>
    );
  }
  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/home"} replace />;
  }
  if (isGuest) {
    return <Navigate to="/home" replace />;
  }
  return <Login />;
}

const SPLASH_KEY = "ston_splash_shown";
const MOBILE_MAX_WIDTH = 768;

export default function App() {
  const [showSplash, setShowSplash] = useState(() => {
    const isMobile = window.innerWidth < MOBILE_MAX_WIDTH;
    const isPublicTrack =
      window.location.pathname.startsWith("/track/") ||
      window.location.pathname.startsWith("/specs/");
    return isMobile && !isPublicTrack && !sessionStorage.getItem(SPLASH_KEY);
  });

  const dismissSplash = useCallback(() => {
    sessionStorage.setItem(SPLASH_KEY, "1");
    setShowSplash(false);
  }, []);

  return (
    <div className="App">
      <div
        className="ston-bg-fixed"
        aria-hidden="true"
        style={{
          backgroundImage:
            "linear-gradient(rgba(18,14,8,0.62), rgba(18,14,8,0.74)), url('" +
            (process.env.PUBLIC_URL || "") +
            "/brand/kitchen.webp')",
        }}
      />
      {showSplash && <SplashScreen onDone={dismissSplash} />}
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
